import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const practices = [
  'Urge surfing',
  'Self-compassion',
  'Emotional awareness',
  'Distress tolerance',
  'Values alignment',
  'Building alternatives',
  'Thought defusion',
  'Environmental design'
];

// Day-specific morning intentions
const getDayContent = () => {
  const dayOfWeek = new Date().getDay();
  const dayContent = {
    0: { // Sunday
      theme: "Rest & Reflection",
      prompt: "Sundays are for rest and reflection. What would make today feel restorative?",
      affirmation: "I give myself permission to rest without guilt.",
      focusQuestion: "What does my body and mind need today?"
    },
    1: { // Monday
      theme: "Fresh Start",
      prompt: "Monday is a fresh beginning. What one small thing can you do today that aligns with who you want to be?",
      affirmation: "Every week is a new opportunity to practice becoming.",
      focusQuestion: "What intention will guide my week?"
    },
    2: { // Tuesday
      theme: "Building Momentum",
      prompt: "Tuesday builds on Monday's momentum. What worked yesterday that you can continue today?",
      affirmation: "Small consistent steps create lasting change.",
      focusQuestion: "How can I build on yesterday's progress?"
    },
    3: { // Wednesday
      theme: "Midweek Check",
      prompt: "Wednesday is halfway through. How are you feeling about your week so far? What needs adjustment?",
      affirmation: "I can adjust my approach without judging my efforts.",
      focusQuestion: "What adjustment would help me finish the week strong?"
    },
    4: { // Thursday
      theme: "Gratitude & Growth",
      prompt: "Thursday invites gratitude. What's one thing you've done this week that you're proud of?",
      affirmation: "I acknowledge my progress, even when it's small.",
      focusQuestion: "What am I proud of this week?"
    },
    5: { // Friday
      theme: "Finishing Strong",
      prompt: "Friday can be challenging—end-of-week fatigue plus weekend ahead. What's your plan?",
      affirmation: "I can enjoy my weekend while staying aligned with my values.",
      focusQuestion: "How will I handle potential triggers this weekend?"
    },
    6: { // Saturday
      theme: "Intentional Living",
      prompt: "Saturdays offer freedom. How will you use this time in ways that serve who you're becoming?",
      affirmation: "Free time is an opportunity, not a threat.",
      focusQuestion: "What meaningful activity will I engage in today?"
    }
  };
  return dayContent[dayOfWeek];
};

export default function MorningCheckIn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const dayContent = getDayContent();
  const [formData, setFormData] = useState({
    energy_level: 5,
    practice: '',
    intention: '',
    daily_focus: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.practice) {
      toast.error('Please select a practice');
      return;
    }
    
    if (!formData.intention.trim()) {
      toast.error('Please share your intention');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/check-ins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: 'morning',
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save check-in');
      }

      toast.success('Your intention is set. You\'ve got this.');
      navigate('/dashboard', { state: { checkInComplete: true } });
    } catch (error) {
      toast.error(error.message || 'Failed to save check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2D3640] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-[#00B4D8]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white" data-testid="morning-checkin-title">
            Morning Check-In
          </h1>
          <p className="text-base text-[#CBD5E0] leading-relaxed">
            {dayContent.theme}
          </p>
        </div>

        {/* Daily Affirmation Card */}
        <div className="bg-gradient-to-br from-[#00B4D8]/20 to-[#06D6A0]/20 rounded-2xl p-5 border border-[#00B4D8]/30">
          <p className="text-center text-white font-medium italic">
            &quot;{dayContent.affirmation}&quot;
          </p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-[#3A4550] rounded-2xl border border-[#4A5568] shadow-lg p-6 space-y-6">
          {/* Daily Prompt */}
          <div className="p-4 bg-[#2D3640] rounded-xl border border-[#4A5568]">
            <p className="text-sm text-[#00B4D8] mb-2 font-medium">Today&apos;s reflection:</p>
            <p className="text-base text-white font-medium">{dayContent.prompt}</p>
          </div>

          {/* Energy Level */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-base font-medium text-white">
                How&apos;s your energy today?
              </Label>
              <span className="text-2xl font-bold text-[#00B4D8]" data-testid="energy-value">
                {formData.energy_level}
              </span>
            </div>
            <Slider
              value={[formData.energy_level]}
              onValueChange={(value) => setFormData({ ...formData, energy_level: value[0] })}
              min={1}
              max={10}
              step={1}
              className="w-full"
              data-testid="energy-slider"
            />
            <div className="flex justify-between text-xs text-[#CBD5E0]">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>

          {/* Practice Selection */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-white">
              Today I&apos;m practicing:
            </Label>
            <Select
              value={formData.practice}
              onValueChange={(value) => setFormData({ ...formData, practice: value })}
            >
              <SelectTrigger className="h-12 rounded-xl border-[#4A5568] bg-[#2D3640] text-white" data-testid="practice-select">
                <SelectValue placeholder="Choose a practice" />
              </SelectTrigger>
              <SelectContent className="bg-[#3A4550] border-[#4A5568]">
                {practices.map((practice) => (
                  <SelectItem key={practice} value={practice} data-testid={`practice-option-${practice}`} className="text-white hover:bg-[#4A5568]">
                    {practice}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Daily Focus Question */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-white">
              {dayContent.focusQuestion}
            </Label>
            <Textarea
              value={formData.daily_focus}
              onChange={(e) => setFormData({ ...formData, daily_focus: e.target.value })}
              placeholder="Take a moment to reflect..."
              className="min-h-20 rounded-xl border-[#4A5568] bg-[#2D3640] text-white placeholder:text-[#A0AEC0] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20 text-base"
              data-testid="daily-focus-input"
            />
          </div>

          {/* Intention */}
          <div className="space-y-2">
            <Label className="text-base font-medium text-white">
              My intention for today:
            </Label>
            <Textarea
              value={formData.intention}
              onChange={(e) => setFormData({ ...formData, intention: e.target.value })}
              placeholder="I intend to..."
              className="min-h-24 rounded-xl border-[#4A5568] bg-[#2D3640] text-white placeholder:text-[#A0AEC0] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20 text-base"
              data-testid="intention-input"
            />
            <p className="text-xs text-[#CBD5E0]">
              2-3 sentences about what you want to focus on today
            </p>
          </div>

          {/* Submit Button */}
          <div className="space-y-3 pt-2">
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 h-auto text-lg font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8]"
              data-testid="submit-checkin-btn"
            >
              {loading ? 'Setting intention...' : 'Set Intention'}
            </Button>
            
            <Button
              type="button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full rounded-full px-6 py-4 h-auto text-base text-[#CBD5E0] hover:text-white hover:bg-[#4A5568]"
              data-testid="cancel-checkin-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
